/**
 * Message Preview Utility
 *
 * Provides smart, channel-aware message preview formatting for ticket/conversation lists.
 * Similar to how Zendesk, Intercom, and Freshdesk display message previews:
 * - Shows appropriate icon/emoji for media types
 * - Truncates text messages intelligently
 * - Handles email subjects specially
 * - Supports i18n translations
 */

import type { MessageType, ChannelType, Conversation } from '../types/chat.types';

/**
 * Message preview configuration
 */
export interface MessagePreviewConfig {
  /** Maximum characters for text preview (default: 50) */
  maxLength?: number;
  /** Include emoji indicator for media types (default: true) */
  showMediaEmoji?: boolean;
  /** Translate function from react-admin (required) */
  translate: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * Icons/emojis for different message types (Zendesk-style)
 */
const MESSAGE_TYPE_ICONS: Record<string, string> = {
  image: '🖼️',
  video: '🎬',
  audio: '🎵',
  document: '📄',
  sticker: '🎨',
  location: '📍',
  live_location: '📍',
  contact: '👤',
  poll: '📊',
  call: '📞',
  system: 'ℹ️',
};

/**
 * Translation keys for message types
 */
const MESSAGE_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  image: 'resources.chat.preview.image',
  video: 'resources.chat.preview.video',
  audio: 'resources.chat.preview.audio',
  document: 'resources.chat.preview.document',
  sticker: 'resources.chat.preview.sticker',
  location: 'resources.chat.preview.location',
  live_location: 'resources.chat.preview.liveLocation',
  contact: 'resources.chat.preview.contact',
  poll: 'resources.chat.preview.poll',
  call: 'resources.chat.preview.call',
  system: 'resources.chat.preview.system',
};


/**
 * Get the display text for a message type
 */
function getMessageTypeLabel(
  type: string,
  translate: (key: string, options?: Record<string, unknown>) => string,
): string {
  const key = MESSAGE_TYPE_TRANSLATION_KEYS[type];
  if (key) {
    return translate(key);
  }
  return type;
}

/**
 * Truncate text intelligently (at word boundary if possible)
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;

  // Try to truncate at word boundary
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > maxLength * 0.7) {
    return truncated.substring(0, lastSpace) + '...';
  }

  return truncated + '...';
}

/**
 * Extract email subject from message metadata or body
 */
function extractEmailSubject(lastMessage: string | null | undefined): string | null {
  if (!lastMessage) return null;

  // Check if the message starts with "Subject:" pattern (from email adapter)
  const subjectMatch = lastMessage.match(/^Subject:\s*(.+?)(?:\n|$)/i);
  if (subjectMatch) {
    return subjectMatch[1].trim();
  }

  return null;
}

/**
 * Clean HTML tags from email body for preview
 */
function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp;
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ') // Collapse whitespace
    .trim();
}

/**
 * Format message preview for display in lists
 *
 * @param conversation - The conversation object with lastMessage and lastMessageType
 * @param config - Preview configuration options
 * @returns Formatted preview string
 *
 * @example
 * // Basic usage
 * const preview = formatMessagePreview(conversation);
 * // "Hello, how can I help you?"
 *
 * @example
 * // With translation
 * const preview = formatMessagePreview(conversation, { translate });
 * // "🖼️ Photo" (for image)
 * // "Re: Order #12345" (for email with subject)
 */
export function formatMessagePreview(
  conversation: Pick<Conversation, 'lastMessage' | 'lastMessageType' | 'channelConnection'>,
  config: MessagePreviewConfig,
): string {
  const { maxLength = 50, showMediaEmoji = true, translate } = config;

  const { lastMessage, lastMessageType, channelConnection } = conversation;
  const channelType = channelConnection?.channel?.type as ChannelType | undefined;
  const messageType = lastMessageType as MessageType | string | undefined;

  // No message
  if (!lastMessage && !messageType) {
    return translate('resources.chat.noMessages');
  }

  // Special handling for vCard/contact card messages
  if (lastMessage?.startsWith('{"ticketzvCard"')) {
    const icon = showMediaEmoji ? '🪪 ' : '';
    const label = translate('resources.chat.preview.contactCard');
    return icon + label;
  }

  // Media message (non-text types)
  if (messageType && messageType !== 'text') {
    const icon = showMediaEmoji ? (MESSAGE_TYPE_ICONS[messageType] || '📎') + ' ' : '';
    const label = getMessageTypeLabel(messageType, translate);

    // If there's also a caption, append truncated version
    if (lastMessage && lastMessage !== `[${messageType}]`) {
      const cleanText = stripHtml(lastMessage);
      if (cleanText.length > 0) {
        return icon + label + ': ' + truncateText(cleanText, maxLength - label.length - 3);
      }
    }

    return icon + label;
  }

  // Email channel - prioritize subject
  if (channelType === 'email') {
    const subject = extractEmailSubject(lastMessage ?? '');
    if (subject) {
      return truncateText(subject, maxLength);
    }

    // If no subject, strip HTML and show body preview
    const cleanBody = stripHtml(lastMessage ?? '');
    return truncateText(cleanBody, maxLength);
  }

  // Text message - strip HTML (in case of email body) and truncate
  const cleanText = stripHtml(lastMessage ?? '');

  // Take first line only (common for WhatsApp/Telegram)
  const firstLine = cleanText.split('\n')[0];

  return truncateText(firstLine, maxLength);
}

/**
 * Get just the message type indicator (icon + label) without the text content
 *
 * Useful for showing "📷 Photo" style badges
 */
export function getMessageTypeIndicator(
  messageType: MessageType | string | null | undefined,
  config: Omit<MessagePreviewConfig, 'maxLength'>,
): string | null {
  if (!messageType || messageType === 'text') return null;

  const { showMediaEmoji = true, translate } = config;
  const icon = showMediaEmoji ? (MESSAGE_TYPE_ICONS[messageType] || '📎') + ' ' : '';
  const label = getMessageTypeLabel(messageType, translate);

  return icon + label;
}

/**
 * Check if a message type requires special preview handling
 */
export function isMediaMessageType(type: MessageType | string | null | undefined): boolean {
  if (!type) return false;
  return type !== 'text' && type !== 'system';
}

/**
 * Format a preview specifically for email conversations
 *
 * Shows: Subject (if available) or first line of body
 */
export function formatEmailPreview(
  lastMessage: string | null | undefined,
  config: MessagePreviewConfig,
): string {
  const { maxLength = 50, translate } = config;

  if (!lastMessage) {
    return translate('resources.email.noSubject');
  }

  const subject = extractEmailSubject(lastMessage);
  if (subject) {
    return truncateText(subject, maxLength);
  }

  const cleanBody = stripHtml(lastMessage);
  return truncateText(cleanBody, maxLength);
}
